import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { SEO_DEFAULTS } from '../../utils/seoConfig';
import { JsonLd } from '../../utils/structuredData';
import { trackEvent } from '../../utils/analytics';
import { useTheme } from '../../contexts/ThemeContext';
import { useInMarketingShell } from '../Marketing/MarketingContext';
import { PlayIcon } from '../CCPlayer/icons';
import {
  SERIES, liveInSeries, liveTutorials, formatDuration,
} from './tutorialConfigs';
import { generateTutorialsHubSchema } from './tutorialSchema';
import './Tutorials.css';

/**
 * TutorialsHubPage — /tutorials/ (design doc agent_docs/
 * cel1157_tutorials_hub_design.md; visual spec = Agent Creative's Direction A
 * "Cinema", implemented pixel-by-pixel per Nitish's ruling — sample source of
 * truth lives in the linked design-review chat, a-cinema/*).
 *
 * Load-bearing rules encoded here:
 *  - NAVIGATE-EVERYWHERE (I2): every card is a LINK to its video page — no
 *    in-place players on the hub. Every card carries { autoplay: true }
 *    navigation state (watch intent); direct/SEO visits keep the poster.
 *  - NO DISCLOSURE CONTROL (Cinema thesis): every live card is always
 *    rendered — the DOM-present crawlability requirement (I1) is satisfied
 *    by construction. Composition is an algorithm keyed only on count:
 *    n = 1 → designed spotlight band; n >= 2 → uniform poster wall
 *    (4/3/2/1 columns by width, pure CSS).
 *  - isLive END-TO-END (D4): sections render only live entries; series with
 *    zero live videos don't render; counts and totals compute from live
 *    entries.
 *  - Hash anchors preserved: section ids = series ids; the hash-scroll effect
 *    below handles cross-page anchor navigation (ScrollToTop exempts hashes).
 *  - Typography: 'Inter Tight' loads via the standard marketing Helmet font
 *    link (same pattern as RoleLandingPage) — it is NOT globally loaded.
 */

/** One poster tile — the uniform wall unit. Poster owns the headline; the
 *  caption prints the task phrase ONCE, small (Cinema finding #1). */
const CinemaTile = ({ t }) => (
  <Link
    to={`/tutorials/${t.slug}/`}
    state={{ autoplay: true }}
    className="cin-tile"
    onClick={() => trackEvent('cta_click', {
      cta_name: 'tutorial_card', page: 'tutorials', location: `series-${t.series}`, video: t.slug,
    })}
  >
    <span className="cin-tile__frame">
      <img
        src={t.thumbnail}
        alt={`${t.title} tutorial thumbnail`}
        className="cin-tile__img"
        loading="lazy"
        width="1280"
        height="720"
      />
      <span className="cin-tile__play" aria-hidden="true"><PlayIcon size={20} /></span>
      <span className="cin-dur cin-tile__dur">{formatDuration(t.durationSeconds)}</span>
    </span>
    <span className="cin-tile__cap">
      <span className="cin-tile__name">{t.title}</span>
    </span>
    <span className="cin-tile__desc">{t.description}</span>
  </Link>
);

/** n = 1: the designed spotlight band (never a stretched card). */
const CinemaSolo = ({ t }) => (
  <div className="cin-solo">
    <Link
      to={`/tutorials/${t.slug}/`}
      state={{ autoplay: true }}
      className="cin-solo__frame"
      onClick={() => trackEvent('cta_click', {
        cta_name: 'tutorial_card', page: 'tutorials', location: `series-${t.series}`, video: t.slug,
      })}
    >
      <img src={t.thumbnail} alt={`${t.title} tutorial thumbnail`} loading="lazy" width="1280" height="720" />
      <span className="cin-play" aria-hidden="true"><PlayIcon size={24} /></span>
      <span className="cin-dur cin-tile__dur">{formatDuration(t.durationSeconds)}</span>
    </Link>
    <div className="cin-solo__body">
      <p className="cin-solo__kicker">The one to watch</p>
      <h3 className="cin-solo__title">{t.title}</h3>
      <p className="cin-solo__copy">{t.description}</p>
      <Link
        to={`/tutorials/${t.slug}/`}
        state={{ autoplay: true }}
        className="cin-cta"
        onClick={() => trackEvent('cta_click', {
          cta_name: 'tutorial_card', page: 'tutorials', location: `series-${t.series}-solo-cta`, video: t.slug,
        })}
      >
        <PlayIcon size={16} />
        Watch {formatDuration(t.durationSeconds)}
      </Link>
    </div>
  </div>
);

/** One series section: numbered editorial header + wall (or spotlight). */
const SeriesSection = ({ series, index }) => {
  const items = liveInSeries(series.id);
  const sectionRef = useRef(null);
  const seenRef = useRef(false);

  // section_view once per page view (existing IO pattern).
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !seenRef.current) {
          seenRef.current = true;
          trackEvent('section_view', { page: 'tutorials', location: `series-${series.id}` });
          io.disconnect();
        }
      });
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, [series.id]);

  if (items.length === 0) return null; // D4: no live videos → no section

  const totalSeconds = items.reduce((sum, t) => sum + t.durationSeconds, 0);
  const minutes = Math.max(1, Math.round(totalSeconds / 60));

  return (
    <section className="cin-series" id={series.id} ref={sectionRef}>
      <div className="cin-series__head">
        <div>
          <span className="cin-series__no">{String(index + 1).padStart(2, '0')} — Series</span>
          <h2 className="cin-series__title">{series.label}</h2>
          <p className="cin-series__blurb">{series.blurb}</p>
        </div>
        <span className="cin-series__meta">
          {items.length} video{items.length === 1 ? '' : 's'} · {minutes} min
        </span>
      </div>
      {items.length === 1 ? (
        <CinemaSolo t={items[0]} />
      ) : (
        <div className="cin-wall">
          {items.map((t) => <CinemaTile key={t.slug} t={t} />)}
        </div>
      )}
    </section>
  );
};

const TutorialsHubPage = () => {
  const navigate = useNavigate();
  const journey = liveTutorials(); // config order IS journey order
  const first = journey[0];
  const totalSeconds = journey.reduce((sum, t) => sum + t.durationSeconds, 0);
  const totalMinutes = Math.max(1, Math.round(totalSeconds / 60));
  const longest = journey.length ? Math.max(...journey.map((t) => t.durationSeconds)) : 0;
  const liveSeries = SERIES.filter((s) => liveInSeries(s.id).length > 0);
  const firstSeries = first ? SERIES.find((s) => s.id === first.series) : null;

  // Theme contract (RoleLandingPage pattern): logged-out marketing shell pins
  // dark (page transparent so the shell's Vanta cells show through);
  // logged-in follows the app theme, light or dark.
  const inShell = useInMarketingShell();
  const { isDarkMode } = useTheme();
  const dark = inShell ? true : isDarkMode;
  const { hash } = useLocation();

  // HASH SCROLL: React Router doesn't scroll to #anchors; ScrollToTop exempts
  // hash destinations (N4.4), and this effect retries until the section has
  // rendered (fresh mounts on cross-page navigation).
  useEffect(() => {
    if (!hash) return undefined;
    const id = hash.slice(1);
    let attempts = 0;
    let timer;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      attempts += 1;
      if (attempts < 12) timer = setTimeout(tryScroll, 60);
    };
    timer = setTimeout(tryScroll, 60);
    return () => clearTimeout(timer);
  }, [hash]);

  return (
    <div className={`tut-page ${dark ? 'dark' : 'light'}${inShell ? ' in-shell' : ''}`}>
      <Helmet>
        <title>CellCog Tutorials: Learn CellCog in Minutes</title>
        <meta
          name="description"
          content="CellCog tutorials, filmed in the real product: hire an AI employee, brief it, and work with it, one short video at a time."
        />
        <link rel="canonical" href={`${SEO_DEFAULTS.url}/tutorials`} />
        <link rel="alternate" type="text/markdown" href={`${SEO_DEFAULTS.url}/tutorials/index.md`} />
        <meta property="og:title" content="CellCog Tutorials: Learn CellCog in Minutes" />
        <meta property="og:description" content="CellCog tutorials, filmed in the real product, one short video at a time." />
        <meta property="og:url" content={`${SEO_DEFAULTS.url}/tutorials/`} />
        <meta property="og:type" content="website" />
        {/* 'Inter Tight' display face — the standard marketing font link
            (RoleLandingPage pattern); NOT globally loaded. */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Inter+Tight:wght@500;600;700;800&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <JsonLd data={generateTutorialsHubSchema()} />

      <div className="cin">
        {/* Hero — editorial, with the library facts (the strongest number we
            have: the whole product in ~N minutes). */}
        <header className="cin-hero">
          <span className="cin-hero__eyebrow">Tutorials</span>
          <h1 className="cin-hero__title">
            CellCog tutorials. Watch a business run on <em>AI employees.</em>
          </h1>
          <p className="cin-hero__sub">
            Short films, shot inside the real product. No slides, no narrator
            promising things: the actual screens, doing the actual work.
          </p>
          <div className="cin-hero__row">
            <span className="cin-hero__facts">
              <b>{journey.length} videos</b>
              <span className="cin-hero__dot" aria-hidden="true">·</span>
              <b>{totalMinutes} minutes</b> end to end
              <span className="cin-hero__dot" aria-hidden="true">·</span>
              longest is {formatDuration(longest)}
            </span>
          </div>
        </header>

        {/* Start here — the ONE obvious thing to do on the page. */}
        {first && (
          <div className="cin-start">
            <Link
              to={`/tutorials/${first.slug}/`}
              state={{ autoplay: true }}
              className="cin-start__poster"
              onClick={() => trackEvent('cta_click', {
                cta_name: 'tutorial_card', page: 'tutorials', location: 'start-here', video: first.slug,
              })}
            >
              <span className="cin-start__ribbon">Start here</span>
              <img src={first.thumbnail} alt={`${first.title} tutorial thumbnail`} width="1280" height="720" />
              <span className="cin-play" aria-hidden="true"><PlayIcon size={28} /></span>
            </Link>
            <div>
              <p className="cin-start__kicker">
                {firstSeries ? firstSeries.label : first.series} · Video 1
              </p>
              <h2 className="cin-start__title">{first.title}</h2>
              <p className="cin-start__copy">{first.description}</p>
              <div className="cin-start__meta">
                {formatDuration(first.durationSeconds)} · Filmed in the real product
              </div>
              <Link
                to={`/tutorials/${first.slug}/`}
                state={{ autoplay: true }}
                className="cin-cta"
                onClick={() => trackEvent('cta_click', {
                  cta_name: 'tutorial_card', page: 'tutorials', location: 'start-here-cta', video: first.slug,
                })}
              >
                <PlayIcon size={16} />
                Play the first video
              </Link>
            </div>
          </div>
        )}

        {/* Series sections, journey order — live entries only (D4). */}
        <main>
          {liveSeries.map((s, i) => <SeriesSection key={s.id} series={s} index={i} />)}
          {journey.length === 0 && (
            <p className="tut-empty">New tutorials are on their way. Check back soon.</p>
          )}
        </main>

        {/* Close band — logged-out acquisition surface only. */}
        {inShell && (
          <footer className="cin-band">
            <h2 className="cin-band__title">Everything here took a minute to set up.</h2>
            <p className="cin-band__sub">Hire AI employees for any role. They form teams, onboard and manage each other.</p>
            <button
              type="button"
              className="cin-cta"
              onClick={() => {
                trackEvent('cta_click', { cta_name: 'tutorial_cta_signup', page: 'tutorials', location: 'close-band' });
                navigate('/signup');
              }}
            >
              Try it free, no credit card needed
            </button>
          </footer>
        )}
      </div>
    </div>
  );
};

export default TutorialsHubPage;
